// Today's work for the Live map: who is cleaning now, who has not started yet and who has
// finished, placed at each site's postcode centre. A cleaner's phone reports where they are only
// when the timer starts and stops, so those two points are what the checks compare with the site.
import { describeWorkLocation, extractUkPostcode } from '@/utils/workLocation';

export const LONDON_TIME_ZONE = 'Europe/London';
// A job running this long past its planned hours, or not started this long after its scheduled
// start, is worth a look.
export const OVERRUN_GRACE_SECONDS = 15 * 60;
export const LATE_GRACE_SECONDS = 15 * 60;
// Timers someone forgot to stop are looked for this many days back.
export const LEFT_RUNNING_DAYS = 7;

// A start or stop the office set says so instead of where the phone was.
export const OFFICE_CHECK = Object.freeze({ status: 'office', label: 'Set by office' });
const hasFlag = (entry, flag) => Array.isArray(entry?.flagCodes) && entry.flagCodes.includes(flag);

const STATE_BY_STATUS = {
	in_progress: 'working',
	assigned: 'waiting',
	completed: 'finished',
};

export const LIVE_MAP_STATUSES = Object.keys(STATE_BY_STATUS);

const dayKeyFormatter = new Intl.DateTimeFormat('en-CA', {
	timeZone: LONDON_TIME_ZONE,
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
});
const timeFormatter = new Intl.DateTimeFormat('en-GB', {
	timeZone: LONDON_TIME_ZONE,
	hour: '2-digit',
	minute: '2-digit',
	hour12: false,
});
const shortDayFormatter = new Intl.DateTimeFormat('en-GB', {
	timeZone: LONDON_TIME_ZONE,
	weekday: 'short',
	day: 'numeric',
	month: 'short',
});
const longDayFormatter = new Intl.DateTimeFormat('en-GB', {
	timeZone: LONDON_TIME_ZONE,
	weekday: 'long',
	day: 'numeric',
	month: 'long',
});

const toDate = (value) => {
	if (!value) return null;
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

// A day key (YYYY-MM-DD) read at midday, so it names the same day in any time zone.
const dayKeyToDate = (dayKey) => toDate(`${dayKey}T12:00:00Z`);

/** Today's date in London as YYYY-MM-DD. */
export const londonDayKey = (date = new Date()) => dayKeyFormatter.format(date);

export const shiftDayKey = (dayKey, days) => {
	const [year, month, day] = String(dayKey).split('-').map(Number);
	return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
};

export const formatLondonTime = (value) => {
	const date = toDate(value);
	return date ? timeFormatter.format(date) : '';
};

const offsetFormatter = new Intl.DateTimeFormat('en-GB', {
	timeZone: LONDON_TIME_ZONE,
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
	hour: '2-digit',
	minute: '2-digit',
	hourCycle: 'h23',
});

// Minutes London is ahead of UTC at that moment: 60 in summer, 0 in winter.
const londonOffsetMinutes = (ms) => {
	const parts = Object.fromEntries(offsetFormatter.formatToParts(new Date(ms)).map(({ type, value }) => [type, value]));
	const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute);
	return Math.round((asUtc - Math.floor(ms / 60000) * 60000) / 60000);
};

/** "09:30" on a London day (YYYY-MM-DD) as an ISO timestamp. */
export const londonTimeToIso = (dayKey, time) => {
	const [year, month, day] = String(dayKey).split('-').map(Number);
	const [hours, minutes] = String(time).split(':').map(Number);
	if (![year, month, day, hours, minutes].every(Number.isFinite)) return null;
	const guess = Date.UTC(year, month - 1, day, hours, minutes);
	const offset = londonOffsetMinutes(guess);
	// On the nights the clocks change the offset can differ either side of the guess.
	const corrected = londonOffsetMinutes(guess - offset * 60000);
	return new Date(guess - corrected * 60000).toISOString();
};

export const formatLondonDay = (dayKey) => {
	const date = dayKeyToDate(dayKey);
	return date ? shortDayFormatter.format(date) : '';
};

export const formatLondonLongDay = (dayKey) => {
	const date = dayKeyToDate(dayKey);
	return date ? longDayFormatter.format(date) : '';
};

/** A running timer, such as 1:02:14. */
export const formatStopwatch = (seconds) => {
	const safe = Math.max(0, Math.floor(Number(seconds) || 0));
	const hours = Math.floor(safe / 3600);
	const minutes = Math.floor((safe % 3600) / 60);
	return `${hours}:${String(minutes).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
};

/** Worked or planned time, such as 2h 05m, 3h or 45m. */
export const formatDuration = (seconds) => {
	const totalMinutes = Math.max(0, Math.round((Number(seconds) || 0) / 60));
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	if (!hours) return `${minutes}m`;
	return minutes ? `${hours}h ${String(minutes).padStart(2, '0')}m` : `${hours}h`;
};

/** "Flat 12", or '' when the job has no flat. */
export const formatFlat = (job) => {
	const flat = String(job?.flat || '').trim();
	if (!flat) return '';
	return /^flat\b/i.test(flat) ? flat : `Flat ${flat}`;
};

/**
 * "Flat 12, Kensington Gardens Square": the flat first, as in a UK address, so a line cut short
 * still shows it.
 */
export const formatSite = (job) => {
	const flat = formatFlat(job);
	return flat ? `${flat}, ${job.siteName}` : job?.siteName || '';
};

export const initialsOf = (name) => {
	const words = String(name || '').trim().split(/\s+/).filter(Boolean);
	if (!words.length) return '?';
	const first = Array.from(words[0]);
	const letters = words.length === 1 ? first.slice(0, 2).join('') : first[0] + Array.from(words.at(-1))[0];
	return letters.toUpperCase();
};

/** { latitude, longitude, accuracy } from a stored timer location, or null. */
export const toLocation = (payload) => {
	if (!payload || typeof payload !== 'object') return null;
	const latitude = Number(payload.latitude ?? payload.lat);
	const longitude = Number(payload.longitude ?? payload.lng);
	if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
	if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
	const accuracy = Number(payload.accuracy);
	return { latitude, longitude, accuracy: Number.isFinite(accuracy) && accuracy >= 0 ? accuracy : null };
};

const timestampOf = (value) => toDate(value)?.getTime() ?? null;

const plannedSecondsOf = (appointment) => {
	if (appointment?.trackTime) return null;
	const hours = Number(appointment?.fixedHours ?? appointment?.hours);
	return Number.isFinite(hours) && hours > 0 ? Math.round(hours * 3600) : null;
};

const workedSecondsOf = (appointment, entries) => {
	const direct = Number(appointment?.actualSeconds);
	if (Number.isFinite(direct) && direct >= 0) return direct;
	const fromEntries = entries.reduce((total, entry) => {
		const seconds = Number(entry?.durationSeconds);
		if (Number.isFinite(seconds) && seconds >= 0) return total + seconds;
		const start = timestampOf(entry?.startAt);
		const end = timestampOf(entry?.endAt);
		return start !== null && end !== null && end > start ? total + Math.floor((end - start) / 1000) : total;
	}, 0);
	return entries.length ? fromEntries : null;
};

const toJob = (appointment, sites, today) => {
	const state = STATE_BY_STATUS[appointment?.status];
	if (!state) return null;
	const day = String(appointment?.date || '').slice(0, 10);
	const startedEarlier = state === 'working' && Boolean(day) && day < today;
	// Today's jobs, and timers left running from an earlier day.
	if (day !== today && !startedEarlier) return null;

	const entries = (Array.isArray(appointment?.timeEntries) ? appointment.timeEntries : [])
		.filter((entry) => entry?.startAt)
		.sort((a, b) => timestampOf(a.startAt) - timestampOf(b.startAt));
	const openEntry = [...entries].reverse().find((entry) => !entry.endAt) || null;
	const firstEntry = entries[0] || null;
	const lastEntry = entries.at(-1) || null;
	const site = sites?.[extractUkPostcode(appointment?.building?.address)] || null;

	// The run whose start counts: the one going now, or the first run of a finished job.
	let timedEntry = null;
	if (state === 'working') timedEntry = openEntry || lastEntry;
	if (state === 'finished') timedEntry = firstEntry;
	const startLocation = toLocation(timedEntry?.startLocation);
	const stopLocation = state === 'finished' ? toLocation(lastEntry?.stopLocation) : null;
	let startCheck = null;
	if (timedEntry) {
		startCheck = hasFlag(timedEntry, 'office_start') ? OFFICE_CHECK : describeWorkLocation({ location: startLocation, site });
	}
	let stopCheck = null;
	if (state === 'finished' && lastEntry?.endAt) {
		stopCheck = hasFlag(lastEntry, 'office_stop') ? OFFICE_CHECK : describeWorkLocation({ location: stopLocation, site });
	}

	const startedAt =
		state === 'waiting' ? null : timedEntry?.startAt || appointment?.actualStartAt || null;
	const endedAt =
		state === 'finished' ? appointment?.actualEndAt || lastEntry?.endAt || appointment?.completedAt || null : null;

	const fallbackPosition = state === 'finished' ? stopLocation || startLocation : startLocation;
	const position = site || fallbackPosition || null;
	const cleaner = appointment?.supplier?.name || 'No cleaner';

	return {
		id: String(appointment?.documentId ?? appointment?.id),
		state,
		day,
		startedEarlier,
		cleaner,
		initials: initialsOf(appointment?.supplier?.name),
		siteName: appointment?.building?.name || 'Unnamed site',
		address: appointment?.building?.address || '',
		flat: appointment?.flatRef || '',
		service: appointment?.service?.name || '',
		plannedSeconds: plannedSecondsOf(appointment),
		scheduledStartAt: appointment?.scheduledStartAt || null,
		startedAt,
		endedAt,
		workedSeconds: state === 'finished' ? workedSecondsOf(appointment, entries) : null,
		startLocation,
		stopLocation,
		startCheck,
		stopCheck,
		site,
		position: position ? { latitude: position.latitude, longitude: position.longitude } : null,
		positionSource: site ? 'site' : position ? 'gps' : null,
	};
};

const STATE_ORDER = { working: 0, waiting: 1, finished: 2 };

const compareJobs = (a, b) => {
	if (a.state !== b.state) return STATE_ORDER[a.state] - STATE_ORDER[b.state];
	if (a.state === 'working') return (timestampOf(a.startedAt) ?? 0) - (timestampOf(b.startedAt) ?? 0);
	if (a.state === 'finished') return (timestampOf(b.endedAt) ?? 0) - (timestampOf(a.endedAt) ?? 0);
	const aDue = timestampOf(a.scheduledStartAt) ?? Number.POSITIVE_INFINITY;
	const bDue = timestampOf(b.scheduledStartAt) ?? Number.POSITIVE_INFINITY;
	if (aDue !== bDue) return aDue - bDue;
	return a.cleaner.localeCompare(b.cleaner);
};

/**
 * @param {object} options
 * @param {Array} options.appointments normalized appointments (today's and earlier running ones)
 * @param {Record<string, {latitude:number, longitude:number}|null>} options.sites postcode centres
 * @param {string} options.today London day key
 */
export const buildLiveMapJobs = ({ appointments = [], sites = {}, today }) =>
	appointments
		.map((appointment) => toJob(appointment, sites, today))
		.filter(Boolean)
		.sort(compareJobs);

export const elapsedSeconds = (job, now = Date.now()) => {
	const started = timestampOf(job?.startedAt);
	if (started === null) return null;
	return Math.max(0, Math.floor((now - started) / 1000));
};

/** What the office may want to look at for a job right now. */
export const jobAlerts = (job, now = Date.now()) => {
	const alerts = [];
	if (job.state === 'working') {
		if (job.startedEarlier) {
			alerts.push({ kind: 'left-running', tone: 'danger', label: `Timer running since ${formatLondonDay(job.day)}` });
		}
		const elapsed = elapsedSeconds(job, now);
		if (job.plannedSeconds && elapsed !== null && elapsed > job.plannedSeconds + OVERRUN_GRACE_SECONDS) {
			alerts.push({ kind: 'over-time', tone: 'warning', label: `${formatDuration(elapsed - job.plannedSeconds)} over planned time` });
		}
	}
	if (job.state === 'waiting' && job.scheduledStartAt) {
		const due = timestampOf(job.scheduledStartAt);
		if (due !== null && now > due + LATE_GRACE_SECONDS * 1000) {
			alerts.push({ kind: 'late', tone: 'warning', label: `Due at ${formatLondonTime(job.scheduledStartAt)}, not started` });
		}
	}
	if (job.startCheck?.status === 'away') {
		alerts.push({ kind: 'away', tone: 'danger', label: `Started ${job.startCheck.label}` });
	}
	if (job.stopCheck?.status === 'away') {
		alerts.push({ kind: 'away', tone: 'danger', label: `Stopped ${job.stopCheck.label}` });
	}
	if (job.startCheck?.status === 'missing') {
		alerts.push({ kind: 'no-location', tone: 'muted', label: 'No location at start' });
	}
	return alerts;
};

export const summarizeLiveMap = (jobs, now = Date.now()) =>
	jobs.reduce(
		(summary, job) => {
			summary[job.state] += 1;
			if (job.state === 'finished' && job.workedSeconds) summary.workedSeconds += job.workedSeconds;
			if (jobAlerts(job, now).length) summary.needsLook += 1;
			return summary;
		},
		{ working: 0, waiting: 0, finished: 0, workedSeconds: 0, needsLook: 0 },
	);
